import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { getService, services } from "../data/services";

const ServiceDetail = () => {
  const { id } = useParams();
  const service = getService(id);

  if (!service) return <Navigate to="/services" replace />;

  const nextIndex = (services.findIndex((s) => s.id === id) + 1) % services.length;
  const next = services[nextIndex];

  return (
    <div data-testid={`service-detail-${service.id}`}>
      {/* Hero */}
      <section className="max-w-7xl mx-auto container-px pt-12 md:pt-16">
        <Link
          to="/services"
          data-testid="service-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-foreground/70 hover:text-primary transition"
        >
          <ArrowLeft className="h-4 w-4" />
          All services
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">Service</p>
            <h1 className="mt-3 font-heading text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02]">
              {service.title}
            </h1>
            <p className="mt-5 text-xl text-foreground/70 max-w-xl italic">
              {service.tagline}
            </p>
            <p className="mt-6 text-lg text-foreground/80 leading-relaxed max-w-xl">
              {service.summary}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                data-testid={`service-cta-enquire-${service.id}`}
                className="rounded-full bg-primary text-primary-foreground h-13 px-7 hover:-translate-y-0.5 transition-transform"
              >
                <Link to={`/contact?service=${encodeURIComponent(service.title)}`}>
                  Enquire about {service.title}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid={`service-cta-all-${service.id}`}
                className="rounded-full h-13 px-7 border-border bg-card hover:bg-secondary"
              >
                <Link to="/services">Browse all services</Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="aspect-[4/5] rounded-[2rem] overflow-hidden ring-1 ring-black/5 shadow-lg">
              <img
                src={service.image}
                alt={service.title}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-7xl mx-auto container-px py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">What's included</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight">
            What you can expect
          </h2>
          <p className="mt-5 text-foreground/70 leading-relaxed">
            {service.what_to_expect}
          </p>
        </div>
        <div className="lg:col-span-7">
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {service.highlights.map((h, i) => (
              <li
                key={i}
                data-testid={`service-highlight-${service.id}-${i}`}
                className="rounded-2xl bg-card border border-border/70 p-5 flex items-start gap-3"
              >
                <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <span className="text-foreground/85 leading-relaxed">{h}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Next service */}
      <section className="max-w-7xl mx-auto container-px pb-24">
        <div className="rounded-[2rem] bg-secondary/60 border border-border/70 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">Next up</p>
            <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-medium">
              Explore <span className="wave-underline">{next.title}</span>
            </h3>
          </div>
          <Button
            asChild
            data-testid={`service-next-${next.id}`}
            className="rounded-full bg-primary text-primary-foreground h-12 px-6"
          >
            <Link to={`/services/${next.id}`}>
              See {next.title}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
